import type { PromotionFailure } from "@/api/publication";

/**
 * The browser's fail-fast check on the signed approval document (#358, #445).
 *
 * A PRE-CHECK, NEVER AN AUTHORITY. The service validates the same three things
 * on the bytes it received and its refusal codes are what the surface reports;
 * this exists so a clinician who picked a Word file, a 40 MB scan or an empty
 * placeholder learns it in the dialog instead of after a multi-megabyte upload.
 * It returns the SAME `PromotionFailure` members the server's codes classify to,
 * so one copy map serves both sides and a locally-refused document and a
 * server-refused one cannot end up saying different things about one problem.
 *
 * ## The order is the service's, and it is load-bearing
 *
 * `manager_evidence.py` states it: size first, so an oversized upload is refused
 * for its SIZE rather than for whatever its first bytes happen to be; then
 * EMPTINESS ahead of the magic bytes, because zero bytes trivially fail a
 * `%PDF-` prefix test and a magic-first reading would tell someone whose upload
 * never left the browser to "pick a PDF" — advice for a file they already picked
 * correctly. Empty belongs with truncated: both mean the document did not
 * arrive, and both recover by sending the same file again.
 *
 * ## Why the magic bytes rather than `File.type`
 *
 * The service judges `%PDF-` and never the declared part type, and the two
 * disagree in both directions: a browser reports an empty `type` for a file
 * dragged from some file managers, and reports `application/pdf` from the
 * extension alone for a renamed document. Trusting `type` would refuse valid
 * documents locally and pass invalid ones to the upload this function exists to
 * avoid. Five bytes is one slice of the file and costs nothing.
 *
 * What it deliberately does NOT check is the `%%EOF` trailer. The service does,
 * and reports a truncated document as `manager_evidence_corrupt`; finding it
 * here would mean reading to the end of a 10 MiB file to reject a case that
 * hardly occurs before an upload, and the server's own refusal already names the
 * recovery.
 */

/** 10 MiB — `manager_evidence.MAX_DOCUMENT_BYTES`, the same cap, stated once. */
export const MAX_MANAGER_EVIDENCE_BYTES = 10 * 1024 * 1024;

/** The ISO 32000-1 file signature the service reads the bytes for. */
const PDF_MAGIC = "%PDF-";

/** The one media type the service accepts, for the file picker's filter. */
export const MANAGER_EVIDENCE_MEDIA_TYPE = "application/pdf";

/**
 * `null` when the document passes every check this side can make.
 *
 * Passing is not acceptance: the service still judges the trailer, the storage
 * and everything the handoff binds around it.
 */
export async function validateManagerEvidence(
  document: File,
): Promise<PromotionFailure | null> {
  if (document.size > MAX_MANAGER_EVIDENCE_BYTES) {
    return { kind: "evidence_too_large" };
  }
  if (document.size === 0) {
    return { kind: "evidence_corrupt" };
  }

  const magic = await document.slice(0, PDF_MAGIC.length).text();
  if (magic !== PDF_MAGIC) {
    return { kind: "evidence_not_pdf" };
  }

  return null;
}
